/**
 * Weave 2 — the loader for the chat bundle.
 *
 * The Weave 2 chat is the VS Code extension's own webview (its Solid app, run against a browser host), built in ncoder-vscode (`packages/web-vscode`) as a
 * self-contained ES module that mounts into a shadow root. It is NOT bundled into the shell: it ships as static
 * files and is imported at runtime, so the shell's build stays React-only and the chat updates without a
 * shell release.
 *
 * Where it lives: `NCODER_PUBLIC_WEAVE2_CHAT_URL` (a full URL of `weave-vscode.js`), else `/weave2-chat/weave-vscode.js`
 * on the shell's own origin — what `scripts/sync-weave2-chat.sh` copies into `public/weave2-chat/`. The workers
 * next to the bundle must be same-origin, which is why the default is the shell's origin.
 */
import env from "@/config/env";
import { getAuthToken } from "@/dataProvider/axios";
import { resolveActiveOrgId, useAuthStoreV1 } from "@/stores/authStore_v1";

export const WEAVE2_CHAT_DEFAULT_URL = "/weave2-chat/weave-vscode.js";

export interface Weave2MountOptions {
  apiBase: string;
  getToken: () => string | undefined;
  getHeaders: () => Record<string, string>;
  sessionID?: string;
  onSessionChange?: (id: string | undefined) => void;
}

export interface Weave2Chat {
  mount: (el: HTMLElement, opts: Weave2MountOptions) => { unmount: () => void };
}

const trimSlash = (s: string) => s.replace(/\/+$/, "");

/** `process.env` is replaced as a whole object at build time (see rspack.config.cjs), so read it defensively. */
export function weave2ChatUrl(publicEnv: Record<string, string | undefined> = process.env): string {
  const url = publicEnv.NCODER_PUBLIC_WEAVE2_CHAT_URL?.trim();
  return url || WEAVE2_CHAT_DEFAULT_URL;
}

/**
 * What the chat needs to reach the session store through the backend proxy
 * (`${API_URL}/proxy/ncoder-engine/v1/...`). Same auth as the Weave client: the user's bearer token, and the
 * ACTIVELY selected org only (never a stored or default one, so a stale value cannot cross tenants). The proxy
 * decides the identity it forwards; the org header is what it checks membership against.
 */
export function weave2MountOptions(extra: Pick<Weave2MountOptions, "sessionID" | "onSessionChange"> = {}): Weave2MountOptions {
  return {
    apiBase: trimSlash(env.API_URL),
    getToken: () => getAuthToken() ?? undefined,
    getHeaders: (): Record<string, string> => {
      const orgId = resolveActiveOrgId(useAuthStoreV1.getState());
      return orgId ? { "X-Organization-ID": orgId } : {};
    },
    ...extra,
  };
}

let loading: Promise<Weave2Chat> | undefined;

/** Imports the bundle once. A failed load is not cached, so a retry after deploying the files works. */
export function loadWeave2Chat(url: string = weave2ChatUrl()): Promise<Weave2Chat> {
  loading ??= import(/* webpackIgnore: true */ /* @vite-ignore */ new URL(url, window.location.href).href)
    .then((m) => {
      if (typeof m.mount !== "function") throw new Error("The Weave 2 chat bundle has no mount()");
      return m as Weave2Chat;
    })
    .catch((e) => {
      loading = undefined;
      throw e;
    });
  return loading;
}
